import {
  CdkPortalOutletAttachedRef,
  ComponentPortal,
  ComponentType,
  Portal,
  TemplatePortal,
} from '@angular/cdk/portal';
import {
  Component,
  ComponentRef,
  EventEmitter,
  HostBinding,
  Inject,
  OnDestroy,
  SimpleChange,
  SimpleChanges,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface ComponentSpec {
  type: ComponentType<unknown>,
  inputs?: Record<string, unknown>
  outputs?: Record<string, (value: unknown) => void>
}

export interface FwPortalDialogData {
  component?: ComponentSpec;
  content?: string;
  template?: TemplateRef<unknown>;
  title?: string;
}

/**
 * @deprecated Legacy Vision: Please upgrade to the newer FwDialogComponent
 */
@Component({
  selector: 'fw-dialog-portal-dialog',
  template: `
      <ng-container *ngIf="title">
          <h1 mat-dialog-title>{{ title }}</h1>
          <button test-id="dialog-close-corner" mat-icon-button mat-dialog-close>
              <mat-icon>close</mat-icon>
          </button>
      </ng-container>
      <mat-dialog-content>
          <p *ngIf="content" class="content">{{ content }}</p>
          <ng-template [cdkPortalOutlet]="portal" (attached)="attached($event)"></ng-template>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
          <button test-id="dialog-close-button" mat-stroked-button mat-dialog-close>
              {{ 'common.actions.close' | translate }}
          </button>
      </mat-dialog-actions>
  `,
  styles: [`
    :host {
      min-width: 200px;
    }

    .content {
      margin-bottom: 30px;
    }
  `],
})
export class FwLegacyPortalDialogComponent implements OnDestroy {
  @HostBinding('attr.class') class = 'mat-dialog-component';
  @HostBinding('attr.test-id') 'test-id' = 'portal-dialog';
  content = this.data.content;
  portal: Portal<unknown>;
  title = this.data.title;

  private destroyed$ = new Subject();

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: FwPortalDialogData,
    private viewContainerRef: ViewContainerRef,
  ) {
    if (this.data.component) {
      this.portal = new ComponentPortal(this.data.component.type, this.viewContainerRef);
    } else if (this.data.template) {
      this.portal = new TemplatePortal(this.data.template, this.viewContainerRef);
    } else {
      throw new Error('One of [component, template] was not provided.');
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(undefined);
  }

  attached(ref: CdkPortalOutletAttachedRef): void {
    if (this.data.component) {
      ref = ref as ComponentRef<never>;
      if (this.data.component.outputs) {
        for (const [key, observer] of Object.entries(this.data.component.outputs)) {
          const output$: EventEmitter<unknown> = ref[key];
          if (typeof output$?.pipe === 'function' && typeof output$.subscribe === 'function') {
            output$.pipe(takeUntil(this.destroyed$)).subscribe(observer);
          }
        }
      }
      if (this.data.component.inputs) {
        const changes: SimpleChanges = {};
        for (const [key, value] of Object.entries(this.data.component.inputs)) {
          ref.instance[key] = value;
          changes[key] = new SimpleChange(value, value, true);
        }
        ref.instance.ngOnChanges?.(changes);
        ref.changeDetectorRef.markForCheck();
      }
    }
  }
}
